import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function RiscattaPremioPage() {
  const nav = useNavigate();

  const [code, setCode] = useState("");
  const [redemption, setRedemption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");

  // Scanner QR
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerStarting, setScannerStarting] = useState(false);
  const [redemptionSource, setRedemptionSource] = useState(null);
  const [qrToken, setQrToken] = useState("");
  const scannerRef = useRef(null);
  const scanHandledRef = useRef(false);

  const stopScanner = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;

    if (scanner) {
      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (err) {
        console.warn("Errore arresto scanner QR:", err);
      }

      try {
        await scanner.clear();
      } catch (err) {
        console.warn("Errore pulizia scanner QR:", err);
      }
    }

    setScannerOpen(false);
    setScannerStarting(false);
  };

  const extractRewardToken = (decodedText) => {
    try {
      const url = new URL(decodedText);

      if (url.hostname !== "gestionale.dallozioroby.it") {
        return null;
      }

      const match = url.pathname.match(
        /^\/reward-confirm\/([^/]+)\/?$/
      );

      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };

  const caricaPremioDaToken = async (token) => {
    setLoading(true);
    setError("");
    setSuccess("");
    setRedemption(null);
    setSelectedProductId("");

    try {
      const res = await authFetch(
        `${API_URL}/api/rewards/redemption/token/${encodeURIComponent(token)}/`
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.detail || "Impossibile verificare il QR."
        );
      }

      setQrToken(token);
      setRedemptionSource("qr");
      setRedemption(data);
      setCode(data?.pickup_code || "");
    } catch (err) {
      setQrToken("");
      setRedemptionSource(null);
      setError(
        err.message || "Errore durante la verifica del QR."
      );
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    if (scannerStarting || scannerOpen) return;

    setError("");
    setSuccess("");
    setRedemption(null);
    setSelectedProductId("");
    setQrToken("");
    setRedemptionSource(null);
    scanHandledRef.current = false;

    setScannerOpen(true);
    setScannerStarting(true);

    // Aspetta che React abbia creato il contenitore #reward-qr-reader.
    await new Promise((resolve) => setTimeout(resolve, 100));

    const scanner = new Html5Qrcode("reward-qr-reader");
    scannerRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText) => {
          if (scanHandledRef.current) return;

          const token = extractRewardToken(decodedText);

          if (!token) {
            setError(
              "QR non valido. Inquadra il QR premio generato dall'app Dallo Zio Roby."
            );
            return;
          }

          scanHandledRef.current = true;

          await stopScanner();
          await caricaPremioDaToken(token);
        },
        () => {
          // Gli errori di scansione mentre la camera cerca un QR
          // sono normali e non vanno mostrati all'utente.
        }
      );

      setScannerStarting(false);
    } catch (err) {
      console.error("Errore avvio scanner QR:", err);

      scannerRef.current = null;

      try {
        await scanner.clear();
      } catch {
        // Nessuna azione necessaria.
      }

      setScannerOpen(false);
      setScannerStarting(false);
      setError(
        "Impossibile avviare la fotocamera. Controlla di aver autorizzato l'accesso alla fotocamera del tablet."
      );
    }
  };

  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;

      if (scanner) {
        try {
          if (scanner.isScanning) {
            scanner.stop().catch(() => {});
          }
        } catch {
          // Cleanup silenzioso allo smontaggio della pagina.
        }
      }
    };
  }, []);
  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
    setRedemption(null);
    setSelectedProductId("");
    setQrToken("");
    setRedemptionSource(null);
    setError("");
    setSuccess("");
  };

  const cercaPremio = async () => {
    if (code.length !== 6) {
      setError("Inserisci il codice ritiro di 6 cifre.");
      return;
    }

    await stopScanner();

    setLoading(true);
    setError("");
    setSuccess("");
    setRedemption(null);
    setSelectedProductId("");
    setQrToken("");
    setRedemptionSource(null);

    try {
      const res = await authFetch(
        `${API_URL}/api/rewards/redemption/code/${code}/`
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || "Impossibile verificare il codice.");
      }

      setRedemption(data);
      setSelectedProductId("");
    } catch (err) {
      setError(err.message || "Errore durante la verifica del codice.");
    } finally {
      setLoading(false);
    }
  };

  const confermaPremio = async () => {
    if (!redemption || confirming) return;

    if (
      redemption.product_selection_required &&
      !selectedProductId
    ) {
      setError("Seleziona il prodotto consegnato.");
      return;
    }

    const clienteNome = `${redemption.cliente?.first_name || ""} ${
      redemption.cliente?.last_name || ""
    }`.trim();

    const selectedProduct = redemption.allowed_products?.find(
      (product) => String(product.id) === String(selectedProductId)
    );

    const prodottoTesto = selectedProduct
      ? `\nProdotto: ${selectedProduct.name}`
      : "";

    const ok = window.confirm(
      `Confermi la consegna di "${redemption.reward?.name}" a ${clienteNome}?${prodottoTesto}`
    );

    if (!ok) return;

    setConfirming(true);
    setError("");
    setSuccess("");

    try {
      const confirmUrl =
        redemptionSource === "qr" && qrToken
          ? `${API_URL}/api/rewards/redemption/token/${encodeURIComponent(qrToken)}/confirm/`
          : `${API_URL}/api/rewards/redemption/code/${code}/confirm/`;

      const res = await authFetch(
        confirmUrl,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            product_id: selectedProductId,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || "Impossibile confermare il premio.");
      }

      setSuccess(data?.detail || "Premio consegnato correttamente.");
      setRedemption(null);
      setSelectedProductId("");
      setCode("");
      setQrToken("");
      setRedemptionSource(null);
    } catch (err) {
      setError(err.message || "Errore durante la conferma del premio.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 820 }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-muted small text-uppercase fw-semibold">
            Fidelity / Punti e Premi
          </div>
          <h1 className="fw-bold mb-1">Riscatta premio</h1>
          <div className="text-muted">
            Inserisci il codice mostrato dal cliente per verificare e consegnare il premio.
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-4"
          onClick={() => nav("/gestione-app-clienti")}
        >
          Torna a Gestione App
        </button>
      </div>

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="mb-4">
            <div className="fw-bold mb-2">Scansiona QR premio</div>

            {!scannerOpen ? (
              <button
                type="button"
                className="btn btn-primary btn-lg w-100"
                onClick={startScanner}
                disabled={loading || scannerStarting}
              >
                Scansiona QR
              </button>
            ) : (
              <>
                <div
                  id="reward-qr-reader"
                  className="border rounded-4 overflow-hidden bg-light"
                  style={{
                    width: "100%",
                    maxWidth: 520,
                    margin: "0 auto",
                  }}
                />

                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={stopScanner}
                  >
                    Annulla scansione
                  </button>
                </div>

                <div className="form-text text-center mt-2">
                  Inquadra il QR premio mostrato nell'app del cliente.
                </div>
              </>
            )}
          </div>

          <div className="d-flex align-items-center gap-3 my-4">
            <div className="border-top flex-grow-1"></div>
            <div className="text-muted fw-semibold">oppure</div>
            <div className="border-top flex-grow-1"></div>
          </div>

          <label className="form-label fw-bold">Codice ritiro</label>

          <div className="d-flex flex-column flex-sm-row gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              className="form-control form-control-lg text-center fw-bold"
              style={{
                fontSize: "2rem",
                letterSpacing: "0.35rem",
                minHeight: 64,
              }}
              value={code}
              onChange={handleCodeChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && code.length === 6) {
                  cercaPremio();
                }
              }}
              placeholder="000000"
            />

            <button
              type="button"
              className="btn btn-dark btn-lg px-4"
              disabled={loading || code.length !== 6}
              onClick={cercaPremio}
            >
              {loading ? "Verifica..." : "Verifica codice"}
            </button>
          </div>

          <div className="form-text mt-2">
            Il codice è composto da 6 cifre ed è associato alla stessa richiesta del QR.
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger rounded-4" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success rounded-4" role="alert">
          <div className="fw-bold">Premio consegnato</div>
          <div>{success}</div>
        </div>
      )}

      {redemption && (
        <div className="card border-0 shadow rounded-4">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
              <div>
                <div className="text-muted small text-uppercase fw-semibold">
                  Cliente
                </div>
                <h3 className="fw-bold mb-1">
                  {redemption.cliente?.first_name}{" "}
                  {redemption.cliente?.last_name}
                </h3>
                <div className="text-muted">
                  {redemption.cliente?.code || ""}
                </div>
              </div>

              <span className="badge text-bg-warning rounded-pill px-3 py-2">
                In attesa
              </span>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-4">
                <div className="border rounded-4 p-3 h-100">
                  <div className="text-muted small">Premio</div>
                  <div className="fw-bold fs-5">
                    {redemption.reward?.name}
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 p-3 h-100">
                  <div className="text-muted small">Costo premio</div>
                  <div className="fw-bold fs-4">
                    {redemption.reward?.required_points} pt
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 p-3 h-100">
                  <div className="text-muted small">Punti cliente</div>
                  <div className="fw-bold fs-4">
                    {redemption.cliente?.loyalty_points} pt
                  </div>
                </div>
              </div>
            </div>

            {redemption.product_selection_required && (
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Prodotto consegnato
                </label>

                <select
                  className="form-select form-select-lg"
                  value={selectedProductId}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setError("");
                  }}
                >
                  <option value="">Seleziona il prodotto...</option>

                  {(redemption.allowed_products || []).map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>

                <div className="form-text">
                  Seleziona il vino effettivamente consegnato al cliente.
                </div>
              </div>
            )}

            <div className="alert alert-warning rounded-4">
              I punti verranno scalati solamente dopo la conferma della consegna.
            </div>

            <button
              type="button"
              className="btn btn-success btn-lg w-100 rounded-3 fw-bold"
              disabled={
                confirming ||
                (redemption.product_selection_required && !selectedProductId)
              }
              onClick={confermaPremio}
            >
              {confirming ? "Conferma in corso..." : "Conferma consegna premio"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
